import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { CalendarCheck, CalendarPlus, Copy, Plus } from 'lucide-react';
import { useState } from 'react';
import { AppointmentCard } from '@/components/appointments/appointment-card';
import { AppointmentForm } from '@/components/appointments/appointment-form';
import { AppointmentSkeleton } from '@/components/appointments/appointment-skeleton';
import { ProtectedRoute } from '@/components/auth/protected-route';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import {
  useAdminUnassignAppointment,
  useAppointments,
  useClaimAppointment,
  useCreateAppointment,
  useDeleteAppointment,
  useUnclaimAppointment,
  useUpdateAppointment,
} from '@/hooks/use-appointments';
import { useAuth } from '@/providers/auth-provider';
import { calendarApi } from '@/services/api';
import type { Appointment, CreateAppointmentRequest } from '@/types/appointment';

export const Route = createFileRoute('/')({
  component: () => (
    <ProtectedRoute>
      <HomePage />
    </ProtectedRoute>
  ),
});

function HomePage() {
  const { session } = useAuth();
  // Branch on the role here so a hero never even mounts the admin
  // components, and therefore never instantiates their admin-only queries.
  return session?.user?.isAdmin ? <AdminHome /> : <HeroHome />;
}

function byStartTime(a: Appointment, b: Appointment) {
  return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
}

// --- Hero ------------------------------------------------------------------

function HeroHome() {
  const { data, isLoading } = useAppointments();
  const claim = useClaimAppointment();
  const release = useUnclaimAppointment();
  const [busyId, setBusyId] = useState<string | null>(null);

  const now = Date.now();
  const appointments = data?.appointments ?? [];

  const freeMissions = appointments
    .filter((a) => a.status === 'unassigned' && !a.mine && new Date(a.startTime).getTime() >= now)
    .sort(byStartTime);

  const myMissions = appointments.filter((a) => a.mine === true).sort(byStartTime);

  // Both actions toggle the same per-card busy flag while their mutation runs;
  // only the mutation differs.
  const runWhileBusy =
    (mutation: { mutateAsync: (id: string) => Promise<unknown> }) => async (appointment: Appointment) => {
      setBusyId(appointment.id);
      try {
        await mutation.mutateAsync(appointment.id);
      } finally {
        setBusyId(null);
      }
    };

  const handleClaim = runWhileBusy(claim);
  const handleRelease = runWhileBusy(release);

  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <h1 className="inline-block border-b-4 border-primary pb-1 text-4xl tracking-wide">My missions</h1>
        {isLoading ? (
          <AppointmentSkeleton />
        ) : myMissions.length === 0 ? (
          <EmptyState
            icon={CalendarCheck}
            title="No missions booked yet"
            description="Claim a free mission below to book a time to see Chris."
          />
        ) : (
          <div className="space-y-3">
            {myMissions.map((appointment) => (
              <AppointmentCard
                key={appointment.id}
                appointment={appointment}
                variant="hero"
                onRelease={handleRelease}
                busy={busyId === appointment.id}
              />
            ))}
          </div>
        )}
      </section>

      <section className="space-y-4">
        <h2 className="inline-block border-b-4 border-accent pb-1 text-3xl tracking-wide">Open missions</h2>
        {isLoading ? (
          <AppointmentSkeleton />
        ) : freeMissions.length === 0 ? (
          <EmptyState
            icon={CalendarPlus}
            title="No free missions right now"
            description="There are no open mission slots at the moment. Please check back later."
          />
        ) : (
          <div className="space-y-3">
            {freeMissions.map((appointment) => (
              <AppointmentCard
                key={appointment.id}
                appointment={appointment}
                variant="hero"
                onClaim={handleClaim}
                busy={busyId === appointment.id}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// --- Admin -----------------------------------------------------------------

function AdminHome() {
  const { data, isLoading } = useAppointments();
  const createMutation = useCreateAppointment();
  const updateMutation = useUpdateAppointment();
  const deleteMutation = useDeleteAppointment();
  const unassignMutation = useAdminUnassignAppointment();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Appointment | undefined>(undefined);
  const [deleting, setDeleting] = useState<Appointment | null>(null);
  const [unassigning, setUnassigning] = useState<Appointment | null>(null);

  const now = Date.now();
  const upcomingSlots = (data?.appointments ?? [])
    .filter((a) => new Date(a.startTime).getTime() >= now)
    .sort(byStartTime);

  const openCreate = () => {
    setEditing(undefined);
    setFormOpen(true);
  };

  const openEdit = (appointment: Appointment) => {
    setEditing(appointment);
    setFormOpen(true);
  };

  const handleSubmit = async (formData: CreateAppointmentRequest) => {
    if (editing) {
      await updateMutation.mutateAsync({ id: editing.id, data: formData });
    } else {
      await createMutation.mutateAsync(formData);
    }
    setFormOpen(false);
    setEditing(undefined);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    await deleteMutation.mutateAsync(deleting.id);
    setDeleting(null);
  };

  const handleUnassign = async () => {
    if (!unassigning) return;
    await unassignMutation.mutateAsync(unassigning.id);
    setUnassigning(null);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="inline-block border-b-4 border-primary pb-1 text-4xl tracking-wide">Mission slots</h1>
        <Button onClick={openCreate} className="gap-2">
          <Plus className="h-4 w-4" />
          Create mission slot
        </Button>
      </div>

      <section className="space-y-4">
        <h2 className="inline-block border-b-4 border-accent pb-1 text-3xl tracking-wide">Upcoming slots</h2>
        {isLoading ? (
          <AppointmentSkeleton />
        ) : upcomingSlots.length === 0 ? (
          <EmptyState
            icon={CalendarPlus}
            title="No upcoming slots"
            description="Create a mission slot so heroes can book a time to see Chris."
            action={
              <Button onClick={openCreate} className="gap-2">
                <Plus className="h-4 w-4" />
                Create mission slot
              </Button>
            }
          />
        ) : (
          <div className="space-y-3">
            {upcomingSlots.map((appointment) => (
              <AppointmentCard
                key={appointment.id}
                appointment={appointment}
                variant="admin"
                onEdit={openEdit}
                onDelete={(a) => setDeleting(a)}
                onUnassign={(a) => setUnassigning(a)}
                busy={
                  (deleteMutation.isPending && deleting?.id === appointment.id) ||
                  (unassignMutation.isPending && unassigning?.id === appointment.id)
                }
              />
            ))}
          </div>
        )}
      </section>

      <section className="space-y-4">
        <h2 className="inline-block border-b-4 border-accent pb-1 text-3xl tracking-wide">Calendar</h2>
        <CalendarFeedCard />
      </section>

      <AppointmentForm
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditing(undefined);
        }}
        onSubmit={handleSubmit}
        appointment={editing}
        mode={editing ? 'edit' : 'create'}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      <AlertDialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this mission slot?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes this mission slot. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={unassigning !== null} onOpenChange={(open) => !open && setUnassigning(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this hero from the slot?</AlertDialogTitle>
            <AlertDialogDescription>
              {unassigning?.assignedToName
                ? `${unassigning.assignedToName} will be removed from this slot and it will become available again.`
                : 'The hero will be removed from this slot and it will become available again.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unassignMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnassign} disabled={unassignMutation.isPending}>
              {unassignMutation.isPending ? 'Removing…' : 'Remove hero'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Admin-only: rendered only inside AdminHome, so the query never fires for heroes.
function CalendarFeedCard() {
  const { session } = useAuth();
  const isAdmin = Boolean(session?.user?.isAdmin);
  const [copied, setCopied] = useState(false);

  const { data } = useQuery({
    queryKey: ['calendar', 'feed-url'],
    queryFn: calendarApi.getFeedUrl,
    enabled: isAdmin,
  });

  const url = data?.calendarUrl ?? '';

  const handleCopy = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Calendar subscription</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2">
          <Input readOnly value={url} placeholder="Loading…" />
          <Button variant="outline" onClick={handleCopy} disabled={!url} className="gap-1">
            <Copy className="h-4 w-4" />
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <Button variant="outline" disabled={!url} asChild className="gap-1 shrink-0">
            <a
              href={`https://calendar.google.com/calendar/u/0/r?cid=${encodeURIComponent(url.replace(/^https?:\/\//, 'webcal://'))}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              + Google
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
